import { Injectable } from '@angular/core';
import { TraceabilityAbstractService } from '~/services/api/web/traceability/traceabilityAbstractService';
import { HttpParams, HttpResponse } from '@angular/common/http';
import { HttpClientWrapper } from '~/services/api/httpClientWrapper';
import { BaseWebService } from '~/services/api/web/base/baseWebService';
import { ResponseModel } from '~/models/responseModel';
import { Observable } from 'rxjs';
import { map as _map } from 'lodash';
import { TraceabilitySummaryModel } from '~/models/traceabilitySummaryModel';
import { EADRequestModel } from '~/models/eadRequestModel';
import { InventoryOrderModel } from '~/models/inventoryOrderModel';
import { InventoryOrderLotModel } from '~/models/inventoryOrderLotModel';
import { InventoryLotModel } from '~/models/inventoryLotModel';
import { VisibilityLotModel } from '~/models/visibilityLotModel';
import { VisibilityOrderModel } from '~/models/visibilityOrderModel';
import { OrderExtractDataModel } from '~/models/orderExtractDataModel';

@Injectable()
export class TraceabilityService extends BaseWebService implements TraceabilityAbstractService {

    public static readonly PATH: string = '/traceability';

    constructor(http: HttpClientWrapper) {
        super(http);
    }

    public postLotVisibility(lot?: VisibilityLotModel): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/lot/visibility';
        let body:any = lot;
        let options:{} = {};

        return this.http.post(url, body, options, (response:HttpResponse<any>) => {
            return new InventoryLotModel(response['data']);
        });
    }

    public postOrderVisibility(order?: VisibilityOrderModel): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/order/visibility';
        let body:any = order;
        let options:{} = {};

        return this.http.post(url, body, options, (response:HttpResponse<any>) => {
            return new InventoryLotModel(response['data']);
        });
    }

    public getOrderTraceabilitySummary(entityId:number, orderId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/order/' + orderId + '/summary';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (lot) => new TraceabilitySummaryModel(lot));
        });
    }

    public getComboTraceabilitySummary(entityId:number, comboId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/combo/' + comboId + '/summary';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (lot) => new TraceabilitySummaryModel(lot));
        });
    }

    public getDownloadFields(): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/order/download/fields';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return response["data"];
        });
    }

    public getDownloadFieldsForCombo(): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/combo/download/fields';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return response["data"];
        });
    }

    public getLotTraceabilitySummary(entityId:number, lotId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/lot/' + lotId + '/summary';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (lot) => new TraceabilitySummaryModel(lot));
        });
    }

    public getBaleTraceabilitySummary(baleId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/bale/' + baleId + '/summary';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (bale) => new TraceabilitySummaryModel(bale));
        });
    }

    public getLotsByOrderId(entityId:number, orderId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/order/' + orderId + '/details';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (lot) => new InventoryOrderLotModel(lot));
        });
    }

    public getLotsByComboId(entityId:number, comboId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/combo/' + comboId + '/details';
        let options:{} = {};
        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return _map(response['data'], (lot) => new InventoryOrderLotModel(lot));
        });
    }

    public getOrderById(entityId:number, orderId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/order/' + orderId;

        let options:{} = {};

        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return new InventoryOrderModel(response['data']);
        });
    }

    public getOrderByComboId(entityId:number, comboId: number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/combo/' + comboId;

        let options:{} = {};

        return this.http.get(url, options, (response: HttpResponse<any>) => {
            return new InventoryOrderModel(response['data']);
        });
    }
    
    public getEadCsv(eadRequestModel: EADRequestModel): Observable<any> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/eadcsv';
        let body:any = eadRequestModel;
        let options:{} = { responseType: 'blob' };
        return this.http.post(url, body, options);
    }

    public getOrderAsCsv(entityId:number, orderId: number, orderExtractDataModel:OrderExtractDataModel[]): Observable<any> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/order/' + orderId + '/download';
        let body:any = orderExtractDataModel;
        let options:{} = { responseType: 'blob' };
        return this.http.post(url, body, options);
    }

    public getOrderAsCsvByComboId(entityId:number, comboId: number, orderExtractDataModel:OrderExtractDataModel[]): Observable<any> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/combo/' + comboId + '/download';
        let body:any = orderExtractDataModel;
        let options:{} = { responseType: 'blob' };
        return this.http.post(url, body, options);
    }

    public getPicRateByMonth(entityId:number, state?:string, edi?:string): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/picratebymonth';
        
        let params = new HttpParams();
        if(state){
            params = params.append('state', state);
        }
        if(edi){
            params = params.append('edi', edi);
        }
        let options = {
            params : params,
        };
        return this.http.get(url, options, (response:HttpResponse<any>) => {
            return response['data'];
        });
    }

    public getPicRateByDay(entityId:number, startDate: string, endDate: string, state?:string, edi?:string): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/picratebyday';
        
        let params = new HttpParams();
        if(state){
            params = params.append('state', state);
        }
        if(edi){
            params = params.append('edi', edi);
        }
        if(startDate){
            params = params.append('start', startDate);
        }
        if(endDate){
            params = params.append('end', endDate);
        }
        let options = {
            params : params,
        };
        return this.http.get(url, options, (response:HttpResponse<any>) => {
            return response['data'];
        });
    }

    public getPicRateByState(entityId:number): Observable<ResponseModel> {
        let url:string = TraceabilityService.URL + TraceabilityService.PATH + '/' + entityId + '/picratebystate';
        let options:{} = {};
        return this.http.get(url, options, (response:HttpResponse<any>) => {
            return response['data'];
        });
    }
}
